/**
 * Glossary — single source of truth for every term, metric, and concept the
 * dashboard surfaces. The same entries power:
 *
 *   1. <InfoTip> popovers that appear next to metric labels and column headers
 *   2. The dedicated /glossary view (GlossaryView.tsx)
 *   3. First-visit OnboardingModal blurbs that pull selected entries by id
 *
 * Adding a new metric to a view? Add the entry here ONCE and reference it by
 * id from anywhere. Don't inline definitions in components — they will drift.
 */

export type GlossaryCategory =
  | 'tiers'
  | 'scoring'
  | 'turnout'
  | 'geography'
  | 'canvass'
  | 'data'
  | 'access';

export type ColorSwatch = {
  /** Tailwind class OR a hex string. Renderer accepts both. */
  color: string;
  label: string;
};

export type GlossaryTerm = {
  id: string;
  /** Short, human-readable display name. */
  title: string;
  /** Plain-English 1-2 sentence summary used in tooltips. */
  short: string;
  /** Full explanation, can be a few paragraphs. Markdown is NOT parsed —
   *  plain text only. Use blank lines for paragraph breaks. */
  long: string;
  /** Optional formula or definition rendered as monospace block. */
  formula?: string;
  /** Optional color swatches (e.g. tier palette). Renders as a chip strip. */
  swatches?: ColorSwatch[];
  /** Optional bullet examples. */
  examples?: string[];
  /** Category drives grouping in the GlossaryView. */
  category: GlossaryCategory;
  /** Optional cross-references to other glossary ids. */
  seeAlso?: string[];
};

// =============================================================================
// Tier vocabulary — Anchor / Hot / Warm / Cool
// =============================================================================

export const TIER_PALETTE = {
  anchor: { hex: '#facc15', label: 'Anchor / Super Voter', range: '75 – 100' },
  hot: { hex: '#dc2626', label: 'Hot', range: '60 – 74' },
  warm: { hex: '#f97316', label: 'Warm', range: '40 – 59' },
  cool: { hex: '#3b82f6', label: 'Cool', range: '0 – 39' },
} as const;

const TIER_SWATCHES: ColorSwatch[] = [
  { color: TIER_PALETTE.anchor.hex, label: 'Anchor 75 – 100' },
  { color: TIER_PALETTE.hot.hex, label: 'Hot 60 – 74' },
  { color: TIER_PALETTE.warm.hex, label: 'Warm 40 – 59' },
  { color: TIER_PALETTE.cool.hex, label: 'Cool 0 – 39' },
];

// =============================================================================
// Term entries
// =============================================================================

export const GLOSSARY: Record<string, GlossaryTerm> = {
  // ---------- TIERS ----------
  tier_overview: {
    id: 'tier_overview',
    title: 'Engagement Tiers',
    short:
      'Every voter is sorted into one of four tiers — Anchor, Hot, Warm, or Cool — based on their 0-100 voter score.',
    long:
      'Engagement tiers turn the raw 0-100 voter score into a four-bucket priority ladder so coordinators and canvassers can talk about voters without staring at numbers.\n\nThe thresholds are the same in the Excel walk-list workbook and in this web dashboard, so a "Hot" voter on paper is the same "Hot" voter on a canvasser\'s phone.\n\nWithin a turf, doors are walked Anchor → Hot → Warm → Cool ("Friendlies-First") so the volunteer hits friendly faces early and builds momentum before any harder conversations.',
    swatches: TIER_SWATCHES,
    category: 'tiers',
    seeAlso: ['voter_score', 'anchor_tier', 'hot_tier', 'warm_tier', 'cool_tier'],
  },
  anchor_tier: {
    id: 'anchor_tier',
    title: 'Anchor / Super Voter (75 – 100)',
    short:
      'Votes in nearly every election, primary and general. Treat as a block-captain candidate first, a vote second.',
    long:
      'Anchors are the highest-engagement voters in the roll — they vote in primaries, generals, and special elections with near-perfect consistency. The historical Excel walk-list called them "Super Voters" interchangeably, and the two terms still appear side by side in the UI.\n\nField strategy: Anchors are not a persuasion target — their vote is already locked. The ROI is recruiting them as block captains who can vouch for the candidate to their neighbors. The walk script for an Anchor is "we need your help recruiting your street," not "we need your vote."\n\nThey are color-coded gold in the dashboard (the same palette used by `super_voter_pct` and the tier legend). The exact count of Anchors in a tenant\'s roll is shown live on the County Overview — don\'t memorize a number, it drifts every import.',
    swatches: [{ color: TIER_PALETTE.anchor.hex, label: 'Anchor' }],
    category: 'tiers',
    seeAlso: ['tier_overview', 'voter_score'],
  },
  hot_tier: {
    id: 'hot_tier',
    title: 'Hot (60 – 74)',
    short:
      'Reliable Republican primary voter. The persuasion-ROI sweet spot — they show up, they just need a reason to pick our candidate.',
    long:
      'Hot voters consistently turn out in Republican primaries but are not yet Anchors. They represent the largest persuasion opportunity in the roll because they will vote — the only question is who they vote for.\n\nField strategy: lead with a substantive policy ask, name the candidate, and offer a specific commitment ("can we count on you?"). These are the doors where a 90-second pitch matters most.',
    swatches: [{ color: TIER_PALETTE.hot.hex, label: 'Hot' }],
    category: 'tiers',
    seeAlso: ['tier_overview'],
  },
  warm_tier: {
    id: 'warm_tier',
    title: 'Warm (40 – 59)',
    short:
      'Votes in general elections but skips primaries. A turnout target, not a persuasion target.',
    long:
      'Warm voters show up in November but rarely in August. The campaign-strategy ask for a Warm voter is "remember to vote in the August primary" — not who to vote for.\n\nField strategy: light touch. Confirm registration, drop the date and polling location, and move on. Saving these doors for late-cycle turnout pushes maximizes ROI.',
    swatches: [{ color: TIER_PALETTE.warm.hex, label: 'Warm' }],
    category: 'tiers',
    seeAlso: ['tier_overview'],
  },
  cool_tier: {
    id: 'cool_tier',
    title: 'Cool (0 – 39)',
    short:
      'Low historical engagement. Deprioritized — knock only when the higher tiers in a turf are exhausted.',
    long:
      'Cool voters either vote rarely, are recently registered with no track record, or are flagged inactive. The walk-list builder still includes them in turfs so the geometry is clean, but they sit at the bottom of the Friendlies-First sort order.\n\nField strategy: knock if you have time. Don\'t skip them entirely — a fresh registration with a thin record can still be a future Anchor — but don\'t budget extra minutes per door either.',
    swatches: [{ color: TIER_PALETTE.cool.hex, label: 'Cool' }],
    category: 'tiers',
    seeAlso: ['tier_overview'],
  },

  // ---------- SCORING ----------
  voter_score: {
    id: 'voter_score',
    title: 'Voter Score (0 – 100)',
    short:
      'Composite priority score. Five weighted components add up to 100 — the higher the score, the higher the campaign ROI.',
    long:
      'Every registered voter in the roll is scored 0 – 100 by combining five components. The score is linear and explainable on purpose — coordinators should be able to look at any voter and reconstruct why they landed where they did.\n\nThe four engagement tiers (Anchor / Hot / Warm / Cool) are simply thresholds applied to this score.',
    formula:
      'score = engagement(40) + recency(20) + primary(15) + party(15) + age(10)',
    examples: [
      'A registered Republican who voted in the last 4 generals + 3 primaries, age 65: ≈ 92 → Anchor',
      'A registered Republican who voted in 2020 + 2022 generals, no primaries, age 45: ≈ 55 → Warm',
      'A new 2023 registration with no voting history, age 28: ≈ 25 → Cool',
    ],
    category: 'scoring',
    seeAlso: [
      'tier_overview',
      'engagement_component',
      'recency_component',
      'primary_component',
      'party_component',
      'age_component',
    ],
  },
  engagement_component: {
    id: 'engagement_component',
    title: 'Engagement (40 pts)',
    short:
      'How often the voter has voted across all available election history. The single largest component.',
    long:
      'Engagement is the count of elections this voter has cast a ballot in, normalized into a 0 – 40 point band. A perfect record across the available history maxes out the band; a single past vote earns a small share.\n\nThis is the strongest predictor of future turnout — past behavior dominates, which is why we let it dominate the score.',
    category: 'scoring',
    seeAlso: ['voter_score'],
  },
  recency_component: {
    id: 'recency_component',
    title: 'Recency (20 pts)',
    short:
      'How recently the voter has voted. A 2024 vote is worth more than a 2018 vote, even if both are "yes" in the engagement column.',
    long:
      'Recency rewards voters whose engagement is current. Points are awarded 0 – 20 based on years-since-most-recent-vote: a vote in the latest cycle maxes the band, a vote from a decade ago earns almost nothing.\n\nThis is the component that catches "they used to vote but stopped," which is exactly the population the Drop-Off Detector exists to find.',
    category: 'scoring',
    seeAlso: ['voter_score', 'drop_off'],
  },
  primary_component: {
    id: 'primary_component',
    title: 'Primary Participation (15 pts)',
    short:
      'How often the voter shows up specifically in primaries. Primary turnout is the leading indicator of partisan loyalty.',
    long:
      'General-election voters cross party lines all the time. Primary voters self-identify — you have to ask for a partisan ballot to participate, so a Republican primary record is the strongest single signal of partisan alignment.\n\nThis component caps out at 15 points (roughly 5 points per primary, capped at three primaries).',
    category: 'scoring',
    seeAlso: ['voter_score'],
  },
  party_component: {
    id: 'party_component',
    title: 'Party Alignment (15 pts)',
    short:
      'Bonus for registered Republicans, smaller bonus for unaffiliated, zero for registered Democrats.',
    long:
      'In Missouri, party affiliation is not recorded at registration the way it is in closed-primary states — the inference comes from primary participation. Where party data is available it gets a 0 – 15 point band. Where it isn\'t, this component falls back on primary history.',
    category: 'scoring',
    seeAlso: ['voter_score', 'primary_component'],
  },
  age_component: {
    id: 'age_component',
    title: 'Age Demographic (10 pts)',
    short:
      'Older voters historically turn out at higher rates. The smallest component on purpose — it\'s a tiebreaker, not a driver.',
    long:
      'Age contributes a small 0 – 10 point band based on Census-style turnout averages by age cohort. We keep it small because it\'s the most stereotype-prone signal in the model — past behavior should dominate, not demographics.',
    category: 'scoring',
    seeAlso: ['voter_score'],
  },
  avg_score: {
    id: 'avg_score',
    title: 'Average Score',
    short:
      'The mean voter score across the rows in this view. Higher = more engaged universe.',
    long:
      'Average score is the simplest health-check for any precinct, subregion, or LD. Compare it to the county average ("Avg Score" in the County Overview) — anything materially above is a friendly turf, anything materially below is a turnout problem.',
    category: 'scoring',
    seeAlso: ['voter_score', 'precinct_comparison'],
  },

  // ---------- TURNOUT / ANALYTICS ----------
  active_voter: {
    id: 'active_voter',
    title: 'Active Voter',
    short:
      'A voter whose registration status is currently active (not inactive, not cancelled, not moved out of state).',
    long:
      'Active status comes straight from the source roll — Missouri marks voters as inactive after two missed federal-election cycles + a non-returned address-confirmation mailer. Inactive voters are still on the roll and can still vote, but they are flagged for cleanup at the next cycle.\n\nIn the dashboard, "Active %" tells you what fraction of a precinct is currently in good standing. A precinct with low active % has either a stale list or a high turnover population — both worth knowing before you assign canvassers.',
    category: 'turnout',
  },
  primary_voter: {
    id: 'primary_voter',
    title: 'Primary Voter',
    short:
      'Has cast a ballot in at least one primary election. Strongest single signal of partisan loyalty in our data.',
    long:
      'Voters who participate in primaries have to ask for a partisan ballot at the polling place — they self-identify in a way that general-election voters don\'t. A "Primary Voter %" of 60% in a precinct means 60% of registered voters there have shown up to a primary at least once in the available history.\n\nThis is the metric that separates Hot from Warm in our scoring.',
    category: 'turnout',
    seeAlso: ['primary_component', 'tier_overview'],
  },
  super_voter_pct: {
    id: 'super_voter_pct',
    title: 'Super Voter %',
    short:
      'Share of voters in this view who landed in the Anchor tier (score 75+).',
    long:
      'Super Voter % is the precinct-level density of Anchors. A precinct with 5%+ Super Voter density is a recruitment goldmine for block captains; a precinct under 1% should be deprioritized for anchor recruitment but may still be a turnout target.',
    category: 'turnout',
    seeAlso: ['anchor_tier', 'tier_overview'],
  },
  roster_turnout: {
    id: 'roster_turnout',
    title: 'Roster Turnout',
    short:
      'Ballots cast in an election divided by voters CURRENTLY registered in that precinct. NOT the same as turnout-of-eligible-population.',
    long:
      'Every "turnout %" number in the dashboard is a roster turnout calculation: ballots / currently-registered. We do not have eligible-population data (citizens of voting age in the precinct), so we can\'t compute the official statewide turnout figure that election authorities publish.\n\nWhat this means in practice: roster turnout will always read higher than the "real" turnout in the news, because the denominator excludes unregistered residents. It\'s still the right metric for comparing precincts to each other and tracking trends inside our roster — just don\'t quote it to a reporter without the caveat.',
    category: 'turnout',
    seeAlso: ['turnout_heatmap', 'sparkline'],
  },
  turnout_heatmap: {
    id: 'turnout_heatmap',
    title: 'Turnout Heatmap',
    short:
      'A precinct × election grid showing roster turnout for each combination, color-coded into five buckets from cold (low) to hot (high).',
    long:
      'The heatmap is the fastest way to spot turnout patterns across many precincts and many elections at once. Each row is a precinct, each column is a past election, each cell is colored by the roster turnout percentage in that precinct for that election.\n\nLook for: vertical bands (an election where every precinct over- or under-performed), horizontal bands (a precinct that consistently over- or under-performs), and recent dropoffs (the right edge of a row going cold).',
    category: 'turnout',
    seeAlso: ['roster_turnout', 'drop_off'],
  },
  drop_off: {
    id: 'drop_off',
    title: 'Drop-Off',
    short:
      'Voters who cast a ballot in a "base year" election but did NOT cast one in a "compare year" election. Re-engagement targets.',
    long:
      'A drop-off voter is by definition reachable — they have voted before, so they know how. They just stopped. The Drop-Off Detector finds these voters for any base/compare election pair so coordinators can build a targeted re-engagement walk list.\n\nDefault pair: 2020 General → 2022 General. A voter who voted in 2020 but not 2022 is a "20→22 dropper." Higher drop-off % in a precinct means more re-engagement opportunity (and possibly more list churn).',
    formula: 'drop_off_pct = dropped_count / base_count × 100',
    category: 'turnout',
    seeAlso: ['roster_turnout', 'turnout_heatmap'],
  },
  sparkline: {
    id: 'sparkline',
    title: 'Sparkline',
    short:
      'Tiny inline chart showing how a precinct\'s turnout has trended across recent elections. One row per precinct.',
    long:
      'Sparklines are the "at a glance" version of the heatmap. They live inside the precinct rollup tables on the LD and Subregion views, so you can scan a precinct list and immediately see who\'s trending up vs trending down without opening a separate view.\n\nA flat line at the top of the chart = consistently high turnout. A nosedive on the right = recent drop-off worth investigating.',
    category: 'turnout',
    seeAlso: ['turnout_heatmap', 'drop_off'],
  },
  precinct_comparison: {
    id: 'precinct_comparison',
    title: 'Precinct Comparison',
    short:
      'A side-by-side table of all precincts in a view, sortable by total voters, average score, primary %, super-voter %, and trend.',
    long:
      'Precinct comparison tables are the bread and butter of the LD, Subregion, and County views. The whole point is to spot the outliers — the precinct with twice the average score, or half the primary participation, or a sparkline that just collapsed.\n\nClick any row to drill into the full PrecinctView for that precinct. Sort by any column header. The "vs county" badge on a metric tells you whether this precinct is above or below the tenant-wide average.',
    category: 'turnout',
    seeAlso: ['avg_score', 'primary_voter', 'super_voter_pct', 'sparkline'],
  },

  // ---------- GEOGRAPHY ----------
  precinct: {
    id: 'precinct',
    title: 'Precinct',
    short:
      'The smallest official voting unit. Every voter in your county belongs to exactly one precinct.',
    long:
      'Precincts are the geographic atom of election administration — they map directly to a polling place and a county election authority\'s tally. Our scoring, walk-list generation, and analytics all aggregate up from precinct level.',
    category: 'geography',
    seeAlso: ['subregion', 'legislative_district'],
  },
  subregion: {
    id: 'subregion',
    title: 'Subregion',
    short:
      'A turf-assignment grouping of one or more precincts (e.g. "12-1" = LD 12, subregion 1). Used for printed walk sheets and coordinator hand-offs.',
    long:
      'Subregions are not an official election-administration concept — we invented them to give coordinators a manageable hand-off unit somewhere between "precinct" (too small) and "legislative district" (too big). The naming convention is `LD-N`, so `12-1` is the first subregion in LD 12.\n\nSubregion membership is tenant data served by `GET /geo/regions` (it\'s a turf construct, not a voter-roll attribute) and rolls up live precinct stats client-side. A tenant whose turfs have not been loaded sees an empty county map rather than another county\'s.',
    category: 'geography',
    seeAlso: ['precinct', 'legislative_district'],
  },
  legislative_district: {
    id: 'legislative_district',
    title: 'Legislative District (LD)',
    short:
      'A state House district. Most counties are split across several LDs.',
    long:
      'Legislative Districts are the official Missouri House boundaries. Every precinct belongs to exactly one LD. Most strategic decisions in the dashboard happen at the LD level because it matches the candidate\'s actual race.',
    category: 'geography',
    seeAlso: ['precinct', 'subregion'],
  },
  turf: {
    id: 'turf',
    title: 'Turf (P##-T#)',
    short:
      'A walkable assignment of ~30 voters in optimized Friendlies-First order. The unit a single canvasser handles in one shift.',
    long:
      'Turfs are the work unit. The format `P##-T#` reads as "precinct 16, turf 3." A turf is built by clustering voters geographically inside their precinct, then sorting them tier-first (Anchor → Hot → Warm → Cool) and, within each tier, in street-serpentine order (down one side of a street and back up the other) so the walk never cuts across mid-street.\n\nEach turf renders as an in-app route map with numbered stops; an optional "Driving directions (Google Maps)" link is available as a secondary hand-off.',
    category: 'geography',
    seeAlso: ['friendlies_first'],
  },
  friendlies_first: {
    id: 'friendlies_first',
    title: 'Friendlies-First Routing',
    short:
      'The rule that turfs are walked Anchor → Hot → Warm → Cool, so the volunteer hits friendly faces early and builds momentum.',
    long:
      'Friendlies-First is the explicit routing decision the platform is built around. Pure nearest-neighbor would be more efficient in raw walking distance, but it would scatter the friendly conversations across the shift and burn volunteer morale. Sorting by tier first, then by distance within tier, keeps the route walkable while front-loading the rewarding doors.',
    category: 'geography',
    seeAlso: ['turf', 'tier_overview'],
  },

  // ---------- CANVASS ----------
  canvass_shift: {
    id: 'canvass_shift',
    title: 'Canvass Shift',
    short:
      'A single time-bounded canvassing session for one canvasser on one route. Lifecycle: pick → briefing → stops → per-door card → AAR → coordinator review.',
    long:
      'A shift is the lifecycle unit of door knocking. It opens when a canvasser starts walking, closes when they end the shift, and produces a knock log + an After Action Review at the end.\n\nShifts are tenant-scoped and canvasser-row-restricted: a canvasser only sees their own shifts; coordinators see every shift in the tenant.',
    category: 'canvass',
    seeAlso: ['briefing', 'aar', 'knock'],
  },
  briefing: {
    id: 'briefing',
    title: 'Stage 1: Briefing',
    short:
      'The pre-walk summary a canvasser sees when they start a shift: route name, door count, tier breakdown, ACP / moved-voter flags, talking points.',
    long:
      'The briefing is the canvasser\'s "what am I about to walk into" screen. It pulls the route\'s voter list and computes a tier breakdown so the canvasser knows up front how many Anchors vs Hot vs Warm vs Cool doors they\'re facing, plus any sensitive flags (ACP / Safe-at-Home participants get a "do not approach" warning).',
    category: 'canvass',
    seeAlso: ['canvass_shift', 'acp_voter'],
  },
  knock: {
    id: 'knock',
    title: 'Knock',
    short:
      'A single recorded interaction at one door. Captures outcome (Talked / Refused / Not Home / Moved / Come Back / Do Not Contact) plus structured response data when outcome is Talked.',
    long:
      'Every door the canvasser visits becomes a knock record, even the ones with no answer. Knocks are linked to a route stop (so we know which voter was knocked) and to a shift (so we can roll them up at the end). Talked-to-voter knocks additionally carry a structured response: who answered, top issues, candidate lean, vote intent, interest in volunteering / donating / yard signs, and an escalation flag.',
    category: 'canvass',
    seeAlso: ['aar', 'canvass_shift', 'door_photo', 'candidate_lean', 'vote_intent'],
  },
  outcome_categories: {
    id: 'outcome_categories',
    title: 'Knock Outcomes',
    short:
      'The six outcome categories every knock falls into: Talked, Refused, Not Home, Moved, Come Back, Do Not Contact.',
    long:
      'Talked = had an actual conversation with the voter or a household member who could speak for them.\nRefused = the voter answered but declined to engage.\nNot Home = no answer at the door.\nMoved = the voter no longer lives at this address.\nCome Back = worth another attempt later (voter was busy, on the phone, stepping out).\nDo Not Contact = voter explicitly asked to be left alone; remove from future walk lists.\n\nOnly "Talked" knocks carry a full structured response. The other five are click-and-move-on so canvassers can clear non-conversation doors fast.',
    category: 'canvass',
    seeAlso: ['knock'],
  },
  aar: {
    id: 'aar',
    title: 'After Action Review (AAR)',
    short:
      'The end-of-shift wrap-up: outcome histogram, avg minutes per door, issue frequency, lean distribution, escalations, free-text notes.',
    long:
      'The AAR is what a canvasser submits when they end a shift. Most of it is auto-computed from the knock log — outcome histogram, average minutes per door, top issues by frequency — so the canvasser only has to confirm the auto-summary and add free-text notes.\n\nAARs are then routed to a coordinator inbox for sign-off. The same prefill rollup is shown to both the canvasser and the coordinator, so there\'s no "what did they actually see" mismatch.',
    category: 'canvass',
    seeAlso: ['canvass_shift', 'knock'],
  },
  acp_voter: {
    id: 'acp_voter',
    title: 'ACP / Safe-at-Home Voter',
    short:
      'A voter participating in Missouri\'s Safe-at-Home Address Confidentiality Program. Their real address is sealed; the briefing flags them as "do not approach."',
    long:
      'Missouri\'s ACP exists to protect victims of stalking, domestic violence, and similar threats by sealing their residential address. These voters appear in the roll with a synthesized "CONFIDENTIAL" placeholder name — they are still on the list but they must not be canvassed door-to-door under any circumstances.\n\nThe briefing screen surfaces a count of any ACP voters on the route so the canvasser knows up front to skip those doors. The count is computed live per route, not hardcoded.',
    category: 'canvass',
    seeAlso: ['briefing'],
  },
  candidate_lean: {
    id: 'candidate_lean',
    title: 'Candidate Lean',
    short:
      'On a Talked knock, the canvasser\'s read on where the voter sits: Strong Us / Lean Us / Undecided / Lean Them / Strong Them / Refused.',
    long:
      'Lean is a six-value scale (five-point scale plus an explicit "Refused" bucket for voters who wouldn\'t say) capturing the canvasser\'s qualitative read after the conversation. "Us" and "Them" are the candidate-agnostic framing the code uses — "Us" means leaning our candidate, "Them" means leaning the opponent. It\'s subjective on purpose — different canvassers will read the same voter differently — but in aggregate it gives the campaign a useful sense of where each precinct stands.\n\nDon\'t over-weight a single lean read. Trust the distribution across many knocks more than any individual data point.',
    category: 'canvass',
    seeAlso: ['knock', 'vote_intent'],
  },
  vote_intent: {
    id: 'vote_intent',
    title: 'Vote Intent',
    short:
      'On a Talked knock, the voter\'s stated plan: Definitely / Probably / Mail-In / Unsure / No / Refused.',
    long:
      'Vote intent is the voter\'s own stated commitment, not the canvasser\'s read. It\'s the most actionable data point in a structured response — "Unsure" voters get a turnout-push call later in the cycle, "Definitely" and "Mail-In" voters get left alone, "No" voters get scrubbed from outreach, and "Refused" voters are left for the next cycle.',
    category: 'canvass',
    seeAlso: ['knock', 'candidate_lean'],
  },
  campaign_issues: {
    id: 'campaign_issues',
    title: 'Campaign Issues',
    short:
      'The tenant-scoped picker list that powers the "top issues" multi-select on a Talked knock. Edited by campaign managers, used by every canvasser.',
    long:
      'Campaign issues are the short list of talking-point topics your canvassers can tag on a Talked knock (taxes, schools, public safety, etc.). They are tenant-scoped — every campaign picks its own list — and ordered by `display_order` so the most-important ones show up first on the card.\n\nOnly ADMIN / ORG_MANAGER / MANAGER can create or edit the list. Canvassers only see it as a read-only picker. Deactivating an issue (toggle `is_active` off) removes it from future knock cards but leaves historical knocks intact for the AAR rollup.',
    category: 'canvass',
    seeAlso: ['knock', 'briefing', 'aar', 'talking_points'],
  },
  talking_points: {
    id: 'talking_points',
    title: 'Talking Points',
    short:
      'Tenant-authored briefing copy shown to canvassers before every shift — candidate bio, what to say at the door, common rebuttals.',
    long:
      'Talking points are the prose half of the Stage 1 briefing. Each one is a title (card headline — e.g. "Taxes — three-sentence version") plus a plain-text body that the canvasser reads before walking their route. They are authored in the Talking Points admin screen by an ADMIN / ORG_MANAGER / MANAGER and show up on every briefing in display_order.\n\nOrthogonal to Campaign Issues: issues are short machine slugs the canvasser tags on a knock response, talking points are the prose that teaches the canvasser how to talk about them. A campaign typically has fewer talking points than issues (maybe three to seven) because each one is read in full at the start of a shift.\n\nDeactivate rather than delete so historical briefings stay reconstructible. The briefing endpoint only returns active rows, but an inactive talking point stays in the database for the audit trail.',
    category: 'canvass',
    seeAlso: ['audit_trail', 'briefing', 'campaign_issues', 'knock'],
  },
  queue_observability: {
    id: 'queue_observability',
    title: 'Field Device Queue',
    short:
      'Real-time view of offline knock queues across canvasser devices.',
    long:
      'The Field Devices widget on the coordinator AAR inbox shows which canvasser phones have queued (unsent) knocks in their local IndexedDB offline store. Each device sends periodic beacon pings reporting its queue depth and last-seen timestamp. Amber badges indicate pending uploads; red badges flag stale devices that haven\'t checked in for over 5 minutes, suggesting the phone may have lost connectivity or died. The widget polls every 30 seconds and can be manually refreshed.',
    category: 'canvass',
    seeAlso: ['field_devices'],
  },
  door_photo: {
    id: 'door_photo',
    title: 'Door Photo',
    short:
      'An optional photo the canvasser attaches to a knock — a snapshot of the door, address placard, or anything noteworthy at the stop.',
    long:
      'On the Stage 3 per-door card, a canvasser can snap a photo using the device camera or choose an existing image. The photo is uploaded after the knock is saved — a two-step process so the knock data itself is never blocked by a slow upload. If the knock falls back to the offline queue (no signal), the photo is skipped; knocks always take priority over photos.\n\nThe backend strips EXIF metadata (including GPS coordinates) before writing the file to disk. This is a compliance requirement — without stripping, a photo of a door with EXIF GPS would leak the voter\'s exact address through any export or audit log.\n\nAccepted formats: JPEG, PNG, WebP. Max size: 10 MB. Photos are served via a /uploads/photos static mount in development and should be fronted by a CDN (CloudFront/nginx) in production.',
    category: 'canvass',
    seeAlso: ['knock', 'canvass_shift'],
  },
  field_devices: {
    id: 'field_devices',
    title: 'Field Devices',
    short:
      'The live heartbeat panel showing every canvasser currently in the field, their offline queue depth, and how recently their phone pinged the backend.',
    long:
      'Each canvasser\'s browser sends a lightweight "beacon" POST to the backend every 60 seconds (and immediately whenever the offline queue count changes). The beacon contains the number of knocks sitting in the browser\'s IndexedDB and the shift the canvasser is on.\n\nThe Field Devices widget in the AAR Inbox aggregates these beacons for the coordinator\'s tenant. A green dot means the device pinged within the last 5 minutes; a red dot means it\'s stale — the phone may have died, lost signal, or the canvasser closed the tab. The amber "N queued" badge shows knocks that haven\'t reached the backend yet; 0 pending is the healthy state.\n\nBeacons are stored as one row per user (upserted on every ping, not a time series) so the table stays small. Rows older than 24 hours can be pruned without losing anything important.',
    category: 'canvass',
    seeAlso: ['aar', 'canvass_shift', 'knock', 'queue_observability'],
  },
  engagement_score: {
    id: 'engagement_score',
    title: 'Engagement Score',
    short:
      'Normalized 0–1 conversion rate: what fraction of knock attempts in a precinct/week resulted in an actual conversation.',
    long:
      'The engagement score is simply conversations ÷ knocks_attempted for a given heatmap cell (precinct × week). 1.0 means every knock produced a conversation; 0.3 means only 30% did — usually because nobody was home, not because people were hostile.\n\nPositive-lean rate is tracked separately (STRONG_US + LEAN_US responses / total conversations) and rendered alongside the cell as its own signal — it is NOT folded into engagement_score. Keep them separate mentally: one measures whether you reached anyone, the other measures how those conversations went.\n\nThe score is only computed for cells with at least min_n knock attempts (k-anonymity gating). Cells below that threshold are suppressed entirely from the heatmap to protect individual privacy.',
    category: 'canvass',
    seeAlso: ['knock', 'turnout_heatmap'],
  },
  stops_map: {
    id: 'stops_map',
    title: 'Stops Map',
    short:
      'The geographic turf view of an active canvass shift — every RouteStop rendered as a numbered pin on an OpenStreetMap base layer, color-coded by knock state.',
    long:
      'During the Stops phase of a shift, canvassers can toggle between a text list and a map surface. The map shows each RouteStop at its geocoded address with the stop sequence number on the pin. Pin colors encode state: red = unknocked target, emerald = knocked, slate = ACP / confidential voter (skip — leave lit only). Tapping a pin opens a popup with the voter name, address, engagement tier, and a "Knock this door" button that routes into the same per-door card the list surface uses.\n\nA polyline overlays the pins in walking order, connecting stop 1 → 2 → 3 → … → N. Legs that still need to be walked render as a bold solid red line; legs the canvasser has already finished (both endpoints knocked) fade to a dashed emerald line so the remaining route stays visually prominent. This gives at-a-glance answers to "where do I go next?" without having to re-read sequence numbers.\n\nStops that have no geocoded coordinates are silently dropped from the map and the toggle strip at the top shows an "N ungeocoded — see list" warning so nobody gets missed. The map auto-fits its viewport to the bounding box of the geocoded stops so canvassers don\'t have to pan to find their turf.\n\nOffline-queued knocks are merged into the same "knocked" state as server-persisted knocks — once you log a door, the pin flips to emerald immediately whether the submit made it to the backend or fell back to the IndexedDB queue.',
    category: 'canvass',
    seeAlso: ['knock', 'canvass_shift', 'acp_voter'],
  },

  // ---------- DATA QUALITY ----------
  audit_trail: {
    id: 'audit_trail',
    title: 'Export Audit Trail',
    short:
      'Automatic logging of bulk voter data reads for compliance.',
    long:
      'Every time an endpoint returns voter PII above a configurable threshold (default 25 rows), an ExportEvent row is written to the audit table with the user ID, tenant, IP address, user agent, export type, row count, and applied filters. This is the provenance trail required by RSMo 115.157 / 115.158 — the election authority can query the export_events table to see exactly who accessed what data and when. Audit logging is best-effort: a failure to write the audit row never blocks the legitimate data request.',
    category: 'data',
    seeAlso: ['talking_points'],
  },
  live_badge: {
    id: 'live_badge',
    title: 'Live Badge',
    short:
      'The green "LIVE" pill means the surrounding numbers are coming from Postgres in real time, not a static snapshot.',
    long:
      'Most of the dashboard is wired live to the backend now, but a few corners (turf geometry, route maps) still come from the static `dashboardData.ts` constants. The Live badge tells you which is which — anything next to a Live badge is fresh from the database, anything without it is from the file.',
    category: 'data',
  },
  data_freshness: {
    id: 'data_freshness',
    title: 'Data Freshness',
    short:
      'The voter roll is current as of the most recent county pull (latest_voter in the DB). Knockback data is real-time.',
    long:
      'The source voter roll is refreshed from your county clerk\'s exports, and the most recent registration date in the file marks the snapshot cutoff. Urban addresses are about 95% accurate; rural records and recent movers are less reliable.\n\nKnockback data, canvass shifts, AARs, and field-device check-ins are recorded live as they happen, so anything generated by canvassers in the field is real-time.\n\nIf a voter\'s address looks wrong on the doorstep, log it as "Moved" — the next import cycle will catch the correction.',
    category: 'data',
  },
  data_changes_added: {
    id: 'data_changes_added',
    title: 'Voters Added',
    short:
      "Voters present in the new file but not the prior one. Includes both newly registered voters and voters new-to-this-county. The system can't always distinguish between those reasons.",
    long:
      "Every voter that shows up in the latest county snapshot but did NOT appear in the snapshot before it counts as \"added\" for the diff. Two underlying reasons drive this: somebody freshly registered, or somebody moved into your county from elsewhere. The clerk's export does not stamp the row with which one it was — the diff only sees \"new ID present\" and records it as added.\n\nIf you need the underlying reason for a specific voter, check their registration date in Voters — a date close to the snapshot date suggests new registration; a much-older date suggests they transferred in from another county.",
    category: 'data',
    seeAlso: ['import_batch', 'data_changes_removed'],
  },
  data_changes_removed: {
    id: 'data_changes_removed',
    title: 'Voters Removed',
    short:
      "Voters present in the prior file but not the new one. The voter row is NOT deleted from the database; it just stops being refreshed. Likely reasons: deceased, moved out of county, or routine list maintenance — but the source file doesn't say which.",
    long:
      "A removed voter is one whose external ID appeared in the prior snapshot but is missing from the latest one. The diff loader records the removal but does not delete the underlying voter row — historical analytics, AAR reports, and previously-built walk lists keep working. The voter just stops being refreshed by future imports.\n\nThe Clerk does not annotate removals with a reason. The most common causes, in rough order: deceased, moved out of county, list maintenance after consecutive missed federal cycles, or a clerical correction. If a voter you expected to see is missing, search the drill-down table by external ID for the removal row.",
    category: 'data',
    seeAlso: ['import_batch', 'data_changes_added'],
  },
  data_changes_modified: {
    id: 'data_changes_modified',
    title: 'Voters Modified',
    short:
      'Voters present in both files where one or more fields changed. Each modified field is its own row in the table below — one voter can show up several times.',
    long:
      'A modified voter is one whose external ID appears in both snapshots but at least one tracked field (status, party, address, precinct, legislative district, registration date, birth year) is different. The diff loader emits one row per changed field, NOT one row per voter — so if a voter moved AND their precinct number changed, you will see two rows for that voter.\n\nThe stat-card count is the count of distinct voters with at least one field change. The drill-down table count below it is the row count, which is always greater-than-or-equal because of the one-row-per-field expansion.',
    category: 'data',
    seeAlso: ['import_batch', 'data_changes_change_reason'],
  },
  data_changes_election_added: {
    id: 'data_changes_election_added',
    title: 'Election Added',
    short:
      "An election that appears in a voter's history in the new file but didn't appear before. Most are newly held elections (e.g. Nov 2024 General); a few are retroactive corrections by the Clerk.",
    long:
      "Voter rows carry a list of past elections they participated in. When a new snapshot arrives, any election that wasn't in the prior list gets recorded as an \"election added\" delta. Most are simply the election that just happened — the Nov 2024 General will show up as added in the first snapshot taken after Nov 2024.\n\nA smaller fraction are retroactive: the Clerk occasionally backfills a voter's record after reconciling provisional ballots, mail-in ballots, or county transfers. Those show up as one-voter election-added entries dated months in the past, and they are real history corrections — not data errors.",
    category: 'data',
    seeAlso: ['import_batch'],
  },
  data_changes_change_reason: {
    id: 'data_changes_change_reason',
    title: 'Change Reason (heuristic)',
    short:
      "Heuristic, not authoritative. The system infers a likely reason from the change pattern (e.g. blank → Unaffiliated → 'recorded after 2024 primary'). Treat as a starting hypothesis, not ground truth.",
    long:
      'The Clerk\'s export does not include a "why this changed" column. The diff loader infers a likely reason from the shape of the change — a status flipping from Active to Inactive after two missed cycles is labeled "list maintenance"; a party going from blank to Unaffiliated right after a primary is labeled "recorded after primary"; an address changing inside the same precinct is labeled "in-precinct move."\n\nThese inferences are a starting hypothesis, NOT ground truth. They are useful for grouping similar changes in summary views but should never be quoted as authoritative. If a voter\'s change reason matters to a real decision (compliance, outreach scrub), confirm with the source-of-truth Clerk record, not this column.',
    category: 'data',
    seeAlso: ['data_changes_modified', 'import_batch'],
  },
  import_batch: {
    id: 'import_batch',
    title: 'Import Batch',
    short:
      'One run of the diff loader against a pair of voter-roll snapshots. Identified by `<new-date>_from_<old-date>`.',
    long:
      'An import batch is the unit of work the diff loader produces. Every time a new voter-roll snapshot is received, the loader compares it to the previous snapshot and emits one batch with all the deltas (added / removed / modified / elections added) plus a summary row.\n\nThe ID convention is `<new-snapshot-date>_from_<old-snapshot-date>`, e.g. `2024-11-15_from_2024-08-30`. That makes batch IDs sortable and self-describing — you can tell at a glance which two snapshots a batch compares without looking up metadata. Only one batch exists per (old, new) pair; rerunning the loader replaces it rather than appending.',
    category: 'data',
    seeAlso: [
      'data_changes_added',
      'data_changes_removed',
      'data_changes_modified',
      'data_changes_election_added',
    ],
  },

  coverage_gap: {
    id: 'coverage_gap',
    title: 'Coverage gap',
    short:
      'A precinct whose contact rate sits well below the rest of your turf — the doors nobody has reached yet.',
    long:
      'Coverage ranks precincts worst-first, so the gaps are already at the top of the list. "Plan routes for the worst 5" hands those five straight to the routes planner with their precinct numbers filled in, which saves copying them across by hand.\n\nThe list it plans from is re-read from the server at the moment you press it, NOT taken off the rows in front of you — the table is sortable and paginated, so what is on screen may be page 3 of a voter-count sort rather than the weakest precincts.\n\nPlanning routes does not close a gap; walking them does. The contact rate will not move until knocks are actually filed against those doors.',
    category: 'canvass',
    seeAlso: ['turf', 'friendlies_first', 'knock'],
  },

  turnout_probability: {
    id: 'turnout_probability',
    title: 'Turnout Probability',
    short:
      'The model\u2019s estimate — 0 to 100% — that this voter casts a ballot in the selected election. A forecast, not a record of anything they did.',
    long:
      'Turnout probability (p_turnout) comes from the stage-1 prediction model, which reads a voter\u2019s own participation history — how often they vote, how recently, and in which election types — and returns the chance they turn out in the election you have selected.\n\nIt is what makes R-Lean Targeting a ranking rather than a list: a precinct full of friendly voters who do not show up is not a priority, and this is the number that says so.\n\nTwo cautions. It is a forecast, so treat it as a sort order rather than a fact about a person; and it is only as current as the last roll import and model run, so it drifts in an off-cycle election. A brand-new registrant has almost no history, which reads as low probability rather than as unknown.',
    category: 'turnout',
    seeAlso: ['r_lean_targeting', 'roster_turnout', 'voter_score'],
  },

  r_lean_targeting: {
    id: 'r_lean_targeting',
    title: 'R-Lean Targeting',
    short:
      'Precinct-level R-lean score multiplied by stage-1 turnout probability — a Tier 1 stopgap to identify high-priority precincts for targeting.',
    long:
      'R-Lean is a precinct-level metric that combines general-election Republican leanness with the probability that voters in that precinct will turn out in an upcoming primary. It is a "stopgap" score because it uses general-election R-lean to predict primary ballot pull, which is a simplification — primary dynamics are different — but it provides a fast way to rank precincts without running a full primary-specific model.\n\nThe composite_score ranks precincts by (R-Lean × p_turnout), so precincts with both high Republican affinity AND high turnout float to the top. Coordinators use this view to decide which precincts to prioritize for canvass targeting or mail.\n\nCaveats:\n• Uses general-election R-lean, not primary R-lean (loses precision for primary matchups).\n• Assumes voter pool is static (does not update as new voters register).\n• Relies on stage-1 turnout model which may be stale in off-cycle elections.',
    category: 'turnout',
    seeAlso: ['precinct', 'precinct_comparison', 'turnout_heatmap'],
  },

  // ---------- ACCOUNTS & ACCESS ----------
  user_role: {
    id: 'user_role',
    title: 'Roles',
    short:
      'A role is the job an account does — what it can see and what it can change. Pick the narrowest one that lets the person do their work.',
    long:
      'Every account has exactly one role. It decides which menu items appear and, more importantly, what the server will let that account read and write — the menu is only a mirror of the real rule.\n\nThe picker offers only the roles YOUR own role is allowed to grant; anything above your ceiling is greyed out rather than hidden, so you can see what exists and ask an admin for it. Only an admin can grant Admin or Org Manager.\n\nRole and turf are separate controls. A role says what kind of work someone does; a region scope says whose doors they do it on. Almost every account should have both set deliberately.',
    examples: [
      'Org Manager — runs the committee or a race end to end: creates accounts, sets up campaigns, sees everything in the tenant.',
      'Coordinator — owns a turf: plans routes, staffs shifts, reviews knocks.',
      'Canvasser — walks doors, sees the voters on their own assigned work and nothing else.',
      'SMS Manager — certifies that a text audience has consent before a batch goes out. NOT the person who runs a candidate\u2019s campaign; that is an Org Manager.',
      'Candidate — one race, one district. Needs a candidate link to work at all (see Candidate login).',
    ],
    category: 'access',
    seeAlso: ['region_scope', 'candidate_login', 'invite_link'],
  },

  roster_template: {
    id: 'roster_template',
    title: 'Team template',
    short:
      'Seeds the Roster step with a typical campaign staff — roles and turf filled in, emails left blank for you to type.',
    long:
      'Three shapes cover almost every campaign: Kitchen Table (the candidate and one person knocking), Field Team (a campaign manager, organizers, and a walking crew), and Full Bench (everything, plus texting and numbers). Picking one adds those rows so you do not have to remember the combination.\n\nA template never invents an email address. It sets the role, the turf, and whether the person may add people of their own; the addresses are yours to type, because an account is a real person.\n\nIt also stays inside your own limits. A role you are not allowed to grant is left out rather than seeded — the card says which one, so you can ask an admin instead of finding out at the Launch step. Turf is always a piece of the candidate’s own district, never blank, because a blank turf is the whole county.\n\nTemplates ADD to what is already on the roster; they never replace it, so a template pressed by mistake costs you a few Remove clicks and no typing. If the team does not fit the seats this tenant has left, the card says so before it adds anything.',
    examples: [
      'First-time candidate with a spouse and a neighbor: Kitchen Table, then Add person twice.',
      'A real race: Field Team, then widen the organizers’ turf to match how you actually split the district.',
    ],
    category: 'access',
    seeAlso: ['user_role', 'region_scope', 'invite_link'],
  },

  region_scope: {
    id: 'region_scope',
    title: 'Region scope',
    short:
      'The turf an account can see. Leaving it empty is the WIDEST setting, not the narrowest — no scope means the whole tenant.',
    long:
      'A region scope binds an account to a Legislative District or a subregion. Every voter list, map, and report that account opens is filtered to it, server-side.\n\nScopes are additive: give someone two turfs and they see the union of both. Changes take effect on their next request — there is no need to have them sign out.\n\nThe trap worth remembering: an account with NO scope rows is not restricted, it is tenant-global. So leaving the field blank on a volunteer hands them the entire county. If your own access is limited to a turf, the system will not let you create an unscoped account at all — you cannot grant more than you hold.\n\nOnly an admin can assign scope. An Org Manager can create the account and set its role, then ask an admin for the turf.',
    examples: [
      'Coordinator for LD 12 — one whole-LD scope.',
      'Canvasser working two subregions — two scope rows, and they see both.',
      'Analyst who reports for the whole committee — no scope, deliberately.',
    ],
    category: 'access',
    seeAlso: ['turf', 'subregion', 'legislative_district', 'user_role'],
  },

  invite_link: {
    id: 'invite_link',
    title: 'Invite link',
    short:
      'A one-time URL that lets a new member choose their own password. Until they use it the account exists but nobody can sign in as it.',
    long:
      'This is how people get into the system. You create the account with a role and a turf; the invitee sets the only credential that will ever authenticate them. Nobody — including you — ever knows their password.\n\nThere is no mailer. The link is shown to you once, on screen, and you deliver it yourself: text, email, or hand it over in person. It is stored only as a hash, so it cannot be looked up afterwards. If it goes missing, issue a new one.\n\nEach link works once and expires in 14 days. Issuing a replacement immediately kills any outstanding link, so one that leaked into an old group chat stops working the moment you re-invite.',
    examples: [
      'New volunteer: create the account, copy the link, text it to them. They pick a password and land straight in the app.',
      'Someone lost access: use Re-invite on their row rather than typing a password for them.',
      'Link expired or leaked: re-invite. The old one dies immediately.',
    ],
    category: 'access',
    seeAlso: ['invite_pending', 'user_role', 'candidate_login'],
  },

  turnout_accuracy: {
    id: 'turnout_accuracy',
    title: 'Model Accuracy',
    short:
      'How the turnout forecast compared to the certified election results, jurisdiction by jurisdiction.',
    long:
      'Before an election, the turnout model writes down its forecast for every voter. After the county certifies its official canvass, those forecasts are added up and set beside the real ballots-cast numbers, at the same grain the county reports them — the canvass jurisdiction, which can group several precincts.\n\nThe calibration ratio is the headline: certified ballots divided by predicted ballots. Above 1.0, the model under-called turnout; below, it over-called it. The per-jurisdiction rows show WHERE it missed, which matters more than the county number — a model that is 2% off everywhere is trustworthy, while one that is perfect on average but 20% off in Parkville is not.\n\nOne mechanic to know: the county reports absentee ballots as a single countywide line, so each jurisdiction’s "certified" figure includes a proportional share of them. The raw election-day count is shown alongside, so nothing is hidden in the allocation.',
    category: 'turnout',
    seeAlso: ['turnout_probability', 'precinct'],
  },

  chase_invites: {
    id: 'chase_invites',
    title: 'Chase invites',
    short:
      'Issues a fresh invite link for everyone who was invited and never signed in. You still hand the links out yourself — there is no mailer.',
    long:
      'A week after a launch the predictable state is "some of the fifteen never signed in", and finding out which meant reading down the Users table. This does that query for you and re-mints in one press.\n\nA new link CANCELS that person\u2019s old one. So by default this only touches people whose link has already expired or was never issued — the ones for whom a replacement costs nothing. Tick "also re-issue links that still work" only when you know nobody is holding a live link, because anyone who is will find theirs dead without being told.\n\nSomeone who has already signed in is never included. They have a working password; replacing it is a per-person decision, not a batch one.\n\nThe list respects your own limits: you only see and touch accounts whose role you could grant and whose turf sits inside yours.',
    category: 'access',
    seeAlso: ['invite_link', 'invite_pending', 'user_role'],
  },

  invite_pending: {
    id: 'invite_pending',
    title: 'Invite pending',
    short:
      'The account has been created but never claimed — it has no working password yet, so nobody can sign in as it.',
    long:
      'An account stays "Invite pending" from the moment you create it until its holder opens their invite link and chooses a password.\n\nIf someone says they cannot get in and their row shows this badge, they never received or never used the link. Re-invite them: it issues a fresh link and revokes the old one.\n\nThe badge clears itself the first time they redeem.',
    category: 'access',
    seeAlso: ['invite_link'],
  },

  candidate_login: {
    id: 'candidate_login',
    title: 'Candidate login',
    short:
      'A candidate\u2019s record is not an account. A candidate login is a user linked to that race, with the candidate\u2019s turf copied onto it.',
    long:
      'Adding someone under Candidates creates the person you are knocking FOR — it does not create a way for them to sign in. The link is what makes an account theirs.\n\nWhen you link them, the candidate\u2019s district is copied onto the login, so they see their own turf and nothing else. That copy is the whole safety mechanism: an account with the Candidate role and no link has no race attached and no turf, and no turf means the entire county — including every other candidate\u2019s numbers.\n\nA campaign started through Campaign Setup already has this: the wizard creates the candidate, the login, and the link together. Use Admin \u2192 Users for a candidate added afterwards.\n\nA linked candidate may also provision their own volunteers, bounded to their own turf. Those volunteers cannot pass the privilege on.',
    examples: [
      'New user \u2192 role Candidate \u2192 pick their race \u2192 send the invite link.',
      'If their district changes later, update it under Candidates — and re-check the login, which does not yet re-sync on its own.',
    ],
    category: 'access',
    seeAlso: ['county_wide_office', 'region_scope', 'invite_link', 'candidate_lean'],
  },

  county_wide_office: {
    id: 'county_wide_office',
    title: 'County-wide office',
    short:
      'A candidate with no district — Sheriff, Assessor — whose login is allowed to see the whole county. It has to be confirmed, because it looks identical to a missing district.',
    long:
      'Most races are district races, so a candidate with no turf is usually a data-entry gap rather than a county-wide office. The system cannot tell those two apart, and the consequence of guessing wrong is severe: the login would see every precinct, including every peer candidate\u2019s data.\n\nSo it asks. Confirm it only when the office really is county-wide.\n\nIf it is a district race, close the drawer, give the candidate their district under Candidates, and come back — the turf will then be copied onto the login automatically.',
    category: 'access',
    seeAlso: ['candidate_login', 'region_scope'],
  },
};

// =============================================================================
// Convenience helpers
// =============================================================================

export function getTerm(id: string): GlossaryTerm | null {
  return GLOSSARY[id] ?? null;
}

export function listTermsByCategory(): Array<{
  category: GlossaryCategory;
  label: string;
  terms: GlossaryTerm[];
}> {
  const labels: Record<GlossaryCategory, string> = {
    tiers: 'Engagement Tiers',
    scoring: 'Voter Scoring',
    turnout: 'Turnout & Analytics',
    geography: 'Geography & Turfs',
    canvass: 'Canvassing & Field Ops',
    data: 'Data Quality',
    access: 'Accounts & Access',
  };
  const order: GlossaryCategory[] = [
    'tiers',
    'scoring',
    'turnout',
    'geography',
    'canvass',
    'data',
    'access',
  ];
  const grouped: Record<string, GlossaryTerm[]> = {};
  for (const term of Object.values(GLOSSARY)) {
    (grouped[term.category] ||= []).push(term);
  }
  return order.map((cat) => ({
    category: cat,
    label: labels[cat],
    terms: (grouped[cat] || []).sort((a, b) => a.title.localeCompare(b.title)),
  }));
}
